import { Button, Chip } from '@nextui-org/react'
import { FC, useCallback, useMemo, useState } from 'react'
import { MdOutlineWarning, MdFileDownload } from 'react-icons/md'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import LogiTable from '../LogiTable'
import Panel from '../Panel'
import Pill from '../Pill'
import { TONES, COURIER } from '../../constants'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { incumplimientosApi, InventoryCell, IncumplimientoEquipoRow } from '../../api'

const columns = [
  { name: 'Id Tradein', uid: 'id_tradein' },
  { name: 'Folio', uid: 'folio' },
  { name: 'Equipo', uid: 'equipo' },
  { name: 'Estado', uid: 'estado' },
  { name: 'Días en estado', uid: 'dias_en_estado' },
  { name: 'Fecha cotización', uid: 'fecha_cotizacion' },
  { name: 'Valor', uid: 'valor' },
  { name: 'Folio bolsa', uid: 'folio_bolsa' },
  { name: 'Ejecutivo', uid: 'ejecutivo' }
]

const STOCK_COLS: Array<{ codigo: InventoryCell['codigo'], label: string }> = [
  { codigo: 'caja', label: 'Cajas' },
  { codigo: 'bolsa_a', label: 'Bolsas A' },
  { codigo: 'bolsa_b', label: 'Bolsas B' },
  { codigo: 'sello', label: 'Sellos' }
]

// Cantidad sugerida para llegar a 1.5× safety (0 si está sobre el umbral)
const sugerir = (stock: InventoryCell[] | undefined, codigo: InventoryCell['codigo']): number => {
  const cell = stock?.find(s => s.codigo === codigo)
  if (cell === undefined || !cell.bajo_safety) return 0
  return Math.max(0, Math.ceil(cell.safety_stock * 1.5) - cell.cantidad_actual)
}

const fmtDateTime = (iso: string | null): string => {
  if (iso === null) return '—'
  const d = new Date(iso)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm} · ${hh}:${min}`
}

const fmtMoney = (n: number | null): string => {
  if (n === null) return '—'
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 })
}

/** Texto de apoyo según la causa probable del diagnóstico. */
const causaHint = (causa: string): string => {
  switch (causa) {
    case 'sin_cajas_ni_sellos':
      return 'La tienda no tiene cajas ni sellos: no puede encajar ni preparar el retiro. Despacha insumos cuanto antes.'
    case 'sin_sellos':
      return 'Sin sellos no se pueden cerrar las bolsas para pasar los equipos a custodia. Despacha sellos.'
    case 'sin_cajas':
      return `Sin cajas no se puede preparar la recolección de ${COURIER}. Despacha cajas.`
    case 'bolsas_a_bajas':
      return 'Las bolsas tipo A están bajo el safety stock. Considera reponer para no frenar el encajado.'
    case 'bolsas_b_bajas':
      return 'Las bolsas tipo B están bajo el safety stock. Considera reponer para no frenar el encajado.'
    case 'bolsas_bajas':
      return 'Las bolsas A y B están bajo el safety stock. Considera reponer ambas.'
    case 'esperando_retiro':
      return `Los equipos ya están encajados y esperan que ${COURIER} los retire. Revisa el estado de la última recolección.`
    default:
      return 'El stock de insumos está OK; el atraso parece operativo. Revisa el flujo de encajado de la tienda.'
  }
}

const TiendaAtrasadaView: FC = () => {
  const { selectedStore, selectedStoreId, openSolicitar } = useLogisticBox()
  const idSeller = useSelectedSeller()
  const [filter, setFilter] = useState<'todos' | 'entregado' | 'custodia'>('todos')

  const fetcher = useCallback(async () => {
    if (selectedStoreId === null) throw new Error('Selecciona una tienda desde incumplimientos')
    return await incumplimientosApi.byStore(selectedStoreId, idSeller, 7)
  }, [selectedStoreId, idSeller])

  const data = useApi(fetcher, [selectedStoreId, idSeller])
  const tienda = data.data?.store ?? selectedStore ?? 'Tienda'

  const equiposFiltrados = useMemo(() => {
    const rows = data.data?.equipos ?? []
    if (filter === 'todos') return rows
    return rows.filter(e => e.estado === filter)
  }, [data.data, filter])

  const renderCell = (e: IncumplimientoEquipoRow, columnKey: string): JSX.Element => {
    switch (columnKey) {
      case 'id_tradein':
        return <span className='font-medium text-xs'>TR-{String(e.id_tradein).padStart(8, '0')}</span>
      case 'folio':
        return <span className='text-xs'>{e.folio ?? '—'}</span>
      case 'equipo':
        return <span className='text-xs'>{e.equipo}</span>
      case 'estado':
        return (
          <Chip size='sm' variant='flat' color={e.estado === 'custodia' ? 'default' : 'primary'}>
            {e.estado === 'custodia' ? 'Custodia' : 'Entregado'}
          </Chip>
        )
      case 'dias_en_estado':
        return (
          <span className='font-medium text-xs' style={{ color: e.dias_en_estado > 14 ? TONES.rosa.ink : TONES.amber.ink }}>
            {e.dias_en_estado} días
          </span>
        )
      case 'fecha_cotizacion':
        return <span className='text-xs'>{fmtDateTime(e.fecha_cotizacion)}</span>
      case 'valor':
        return <span className='text-xs'>{fmtMoney(e.valor)}</span>
      case 'folio_bolsa':
        return <span className='text-xs'>{e.folio_bolsa ?? <span className='text-gray-400'>—</span>}</span>
      case 'ejecutivo':
        return <span className='text-xs'>{e.ejecutivo ?? '—'}</span>
      default:
        return <span className='text-xs'>{(e as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      <BackButton to='incumplimientos' />

      <Panel>
        <ApiStateView loading={data.loading} error={data.error} onRetry={data.reload}>
          <div className='flex items-start justify-between gap-6 flex-wrap'>
            <div>
              <h1 className='text-xl font-bold text-[#151930]'>{tienda}</h1>
              {(data.data?.contacto_nombre !== null || data.data?.contacto_telefono !== null) && (
                <p className='text-xs text-[#151930]/60 mt-1'>
                  {data.data?.contacto_nombre !== null && data.data?.contacto_nombre !== undefined && (
                    <>Encargado: <b>{data.data.contacto_nombre}</b></>
                  )}
                  {data.data?.contacto_telefono !== null && data.data?.contacto_telefono !== undefined && (
                    <> · {data.data.contacto_telefono}</>
                  )}
                </p>
              )}
              <p className='text-xs text-[#151930]/60 mt-0.5'>
                Última recolección {COURIER}:{' '}
                <b>{fmtDateTime(data.data?.last_picked_up_at ?? null)}</b>
                {data.data?.dias_desde_ultimo_retiro !== null && data.data?.dias_desde_ultimo_retiro !== undefined && (
                  <> · hace {data.data.dias_desde_ultimo_retiro} días</>
                )}
                {(data.data?.dias_desde_ultimo_retiro ?? 0) > 14 && (
                  <span className='ml-2'><Pill tone='rosa'>Excede 14 días</Pill></span>
                )}
              </p>
            </div>
          </div>

          {data.data !== null && data.data.equipos.length > 0 && (
            <div
              className='mt-4 rounded-xl p-3 border'
              style={{ background: TONES[data.data.diagnostico.tone].bgSoft, borderColor: `${TONES[data.data.diagnostico.tone].ink}4d` }}
            >
              <div className='flex items-start gap-3'>
                <MdOutlineWarning className='shrink-0 text-xl mt-0.5' style={{ color: TONES[data.data.diagnostico.tone].ink }} />
                <div className='flex-1 text-xs'>
                  <span className='font-semibold' style={{ color: TONES[data.data.diagnostico.tone].ink }}>
                    Causa probable: {data.data.diagnostico.label}.{' '}
                  </span>
                  <span className='text-[#151930]/70'>{causaHint(data.data.diagnostico.causa)}</span>
                </div>
              </div>
              <div className='mt-3 flex flex-wrap items-center gap-2 pl-8'>
                {STOCK_COLS.map(c => {
                  const cell = data.data?.stock?.find(s => s.codigo === c.codigo)
                  if (cell === undefined) return null
                  const color = cell.bajo_safety ? TONES.rosa.ink : '#151930'
                  return (
                    <span
                      key={c.codigo}
                      className='inline-flex items-center gap-1 rounded-lg bg-white/70 px-2 py-1 text-[14px] border'
                      style={{ borderColor: cell.bajo_safety ? `${TONES.rosa.ink}4d` : '#e5e7eb' }}
                    >
                      <span className='text-[#151930]/60'>{c.label}:</span>
                      <span className='font-semibold' style={{ color }}>{cell.cantidad_actual}</span>
                      <span className='text-gray-400'>/ {cell.safety_stock}</span>
                    </span>
                  )
                })}
                {selectedStoreId !== null && (
                  <Button
                    size='sm'
                    className='ml-auto bg-[#151930] text-white text-xs font-semibold'
                    onPress={() => openSolicitar({
                      tienda: tienda,
                      idStore: selectedStoreId,
                      cajas: sugerir(data.data?.stock, 'caja'),
                      bolsasA: sugerir(data.data?.stock, 'bolsa_a'),
                      bolsasB: sugerir(data.data?.stock, 'bolsa_b'),
                      sellos: sugerir(data.data?.stock, 'sello')
                    })}
                  >
                    Despachar insumos ya
                  </Button>
                )}
              </div>
            </div>
          )}
        </ApiStateView>
      </Panel>

      <Panel>
        <div className='flex items-center justify-between mb-4 flex-wrap gap-3'>
          <h2 className='font-semibold text-[#151930]'>
            Equipos atrasados · {data.data?.equipos.length ?? 0} equipos &gt; 7 días
          </h2>
          <div className='flex gap-2'>
            <select
              className='border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white'
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
            >
              <option value='todos'>Todos los estados</option>
              <option value='entregado'>Entregado</option>
              <option value='custodia'>Custodia</option>
            </select>
            <Button variant='bordered' className='border-gray-200 text-xs' startContent={<MdFileDownload className='text-base' />}>Exportar</Button>
          </div>
        </div>

        <ApiStateView
          loading={data.loading}
          error={data.error}
          onRetry={data.reload}
          empty={!data.loading && equiposFiltrados.length === 0}
          emptyText='Sin equipos atrasados con ese filtro'
        >
          <LogiTable<IncumplimientoEquipoRow>
            columns={columns}
            items={equiposFiltrados}
            renderCell={renderCell}
            rowKey={(e) => e.id_tradein}
            ariaLabel='Equipos atrasados de la tienda'
            loading={data.loading}
            emptyText='Sin equipos atrasados con ese filtro'
            rowsPerPage={10}
            bottomExtra={
              <div className='flex items-center justify-between text-xs text-[#151930]/60'>
                <span>Mostrando {equiposFiltrados.length} de {data.data?.equipos.length ?? 0} equipos</span>
              </div>
            }
          />
        </ApiStateView>
      </Panel>
    </div>
  )
}

export default TiendaAtrasadaView
